/**
 * iter357 — Le collaborateur ouvre un dossier de A à Z depuis son poste :
 * véhicule connu, ou création du véhicule et du client. Aucun prix ici.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Car, UserPlus, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const champ = (label, props, testid) => (
  <div>
    <Label className="text-[11px] uppercase tracking-wider text-gray-500">{label}</Label>
    <Input className="h-11" data-testid={testid} {...props} />
  </div>
);

export const NouveauDossierDialog = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [etape, setEtape] = useState("recherche"); // recherche | creation
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [choisi, setChoisi] = useState(null);
  const [motif, setMotif] = useState("");
  const [km, setKm] = useState("");
  const [busy, setBusy] = useState(false);

  // création
  const [veh, setVeh] = useState({ plaque: "", marque: "", modele: "", vin: "", premiere_mec: "" });
  const [qc, setQc] = useState("");
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState(null); // client existant choisi
  const [cli, setCli] = useState({ nom: "", prenom: "", tel_principal: "", email: "" });

  useEffect(() => {
    if (!open) return;
    setEtape("recherche"); setQ(""); setItems([]); setChoisi(null);
    setMotif(""); setKm(""); setClient(null); setQc(""); setClients([]);
    setVeh({ plaque: "", marque: "", modele: "", vin: "", premiere_mec: "" });
    setCli({ nom: "", prenom: "", tel_principal: "", email: "" });
  }, [open]);

  useEffect(() => {
    if (!open || q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => {
      api.get("/atelier/vehicules", { params: { q } })
        .then(({ data }) => setItems(data.items || []))
        .catch(() => setItems([]));
    }, 250);
    return () => clearTimeout(t);
  }, [open, q]);

  useEffect(() => {
    if (etape !== "creation" || qc.trim().length < 2) { setClients([]); return; }
    const t = setTimeout(() => {
      api.get("/atelier/clients", { params: { q: qc } })
        .then(({ data }) => setClients(data.items || []))
        .catch(() => setClients([]));
    }, 250);
    return () => clearTimeout(t);
  }, [etape, qc]);

  const creer = async () => {
    const corps = {
      notes_client: motif.trim() || null,
      km_depose: km ? parseInt(km, 10) : null,
    };
    if (etape === "recherche") {
      if (!choisi) { toast.error("Choisis un véhicule"); return; }
      corps.vehicule_id = choisi.id;
    } else {
      if (!veh.plaque.trim()) { toast.error("Indique la plaque"); return; }
      if (!client && !cli.nom.trim()) { toast.error("Indique le client"); return; }
      corps.vehicule_nouveau = veh;
      if (client) corps.client_id = client.id;
      else corps.client_nouveau = cli;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/atelier/nouveau-dossier", corps);
      toast.success(`Dossier ${data.numero} ouvert`);
      onClose?.();
      navigate(`/atelier/travail/${data.or_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Création impossible");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="nouveau-dossier-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Car className="w-4 h-4 text-[#52a675]" /> Nouveau dossier
          </DialogTitle>
        </DialogHeader>

        {etape === "recherche" && (
          <>
            <Input
              value={q} onChange={(e) => { setQ(e.target.value); setChoisi(null); }}
              placeholder="Plaque, marque ou modèle…" className="h-11"
              data-testid="nouveau-dossier-recherche"
            />

            {!choisi && (
              <div className="max-h-44 overflow-y-auto space-y-1" data-testid="nouveau-dossier-resultats">
                {items.map((v) => (
                  <button
                    key={v.id} onClick={() => setChoisi(v)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-[#52a675] text-sm"
                    data-testid={`nouveau-dossier-vehicule-${v.id}`}
                  >
                    <div className="font-mono text-xs font-bold">{v.plaque}</div>
                    <div className="text-gray-700">{`${v.marque || ""} ${v.modele || ""}`.trim()}</div>
                    <div className="text-[11px] text-gray-500">{v.client_nom}</div>
                  </button>
                ))}
                {q.trim().length >= 2 && items.length === 0 && (
                  <div className="text-xs text-gray-400 text-center py-2">Aucun véhicule trouvé</div>
                )}
              </div>
            )}

            {choisi && (
              <div className="p-3 rounded-xl border-2 border-[#52a675]/40 bg-[#52a675]/5" data-testid="nouveau-dossier-choisi">
                <div className="font-mono text-xs font-bold">{choisi.plaque}</div>
                <div className="text-sm">{`${choisi.marque || ""} ${choisi.modele || ""}`.trim()}</div>
                <div className="text-[11px] text-gray-500">{choisi.client_nom}</div>
              </div>
            )}

            <Button
              variant="outline" onClick={() => setEtape("creation")}
              className="h-10 border-dashed" data-testid="nouveau-dossier-creer-vehicule"
            >
              <UserPlus className="w-4 h-4 mr-1" /> Véhicule inconnu — le créer
            </Button>
          </>
        )}

        {etape === "creation" && (
          <div className="space-y-3" data-testid="nouveau-dossier-creation">
            <button
              onClick={() => setEtape("recherche")}
              className="text-xs text-gray-500 inline-flex items-center gap-1"
              data-testid="nouveau-dossier-retour"
            >
              <ArrowLeft className="w-3 h-3" /> Rechercher un véhicule existant
            </button>

            <div className="text-[11px] uppercase tracking-wider text-gray-400">Véhicule</div>
            {champ("Plaque", {
              value: veh.plaque, placeholder: "GE 123456",
              onChange: (e) => setVeh({ ...veh, plaque: e.target.value }),
            }, "nouveau-vehicule-plaque")}
            <div className="grid grid-cols-2 gap-2">
              {champ("Marque", {
                value: veh.marque, onChange: (e) => setVeh({ ...veh, marque: e.target.value }),
              }, "nouveau-vehicule-marque")}
              {champ("Type / modèle", {
                value: veh.modele, onChange: (e) => setVeh({ ...veh, modele: e.target.value }),
              }, "nouveau-vehicule-modele")}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {champ("VIN", {
                value: veh.vin, onChange: (e) => setVeh({ ...veh, vin: e.target.value }),
              }, "nouveau-vehicule-vin")}
              {champ("1re mise en circ.", {
                type: "date", value: veh.premiere_mec,
                onChange: (e) => setVeh({ ...veh, premiere_mec: e.target.value }),
              }, "nouveau-vehicule-premiere-mec")}
            </div>

            <div className="text-[11px] uppercase tracking-wider text-gray-400 pt-1">Client</div>
            <Input
              value={qc} onChange={(e) => { setQc(e.target.value); setClient(null); }}
              placeholder="Chercher un client existant…" className="h-11"
              data-testid="nouveau-client-recherche"
            />
            {!client && clients.length > 0 && (
              <div className="max-h-32 overflow-y-auto space-y-1">
                {clients.map((c) => (
                  <button
                    key={c.id} onClick={() => setClient(c)}
                    className="w-full text-left px-3 py-2 rounded-lg border border-gray-200 hover:border-[#52a675] text-sm"
                    data-testid={`nouveau-client-${c.id}`}
                  >
                    {c.label}
                    <span className="text-[11px] text-gray-500 ml-2">{c.tel_principal}</span>
                  </button>
                ))}
              </div>
            )}
            {client ? (
              <div className="p-3 rounded-xl border-2 border-[#52a675]/40 bg-[#52a675]/5 text-sm" data-testid="nouveau-client-choisi">
                {client.label}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-2">
                  {champ("Nom", {
                    value: cli.nom, onChange: (e) => setCli({ ...cli, nom: e.target.value }),
                  }, "nouveau-client-nom")}
                  {champ("Prénom", {
                    value: cli.prenom, onChange: (e) => setCli({ ...cli, prenom: e.target.value }),
                  }, "nouveau-client-prenom")}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {champ("Téléphone", {
                    value: cli.tel_principal, inputMode: "tel",
                    onChange: (e) => setCli({ ...cli, tel_principal: e.target.value }),
                  }, "nouveau-client-tel")}
                  {champ("E-mail", {
                    value: cli.email, onChange: (e) => setCli({ ...cli, email: e.target.value }),
                  }, "nouveau-client-email")}
                </div>
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2 pt-1">
          {champ("Km au dépôt", {
            value: km, inputMode: "numeric", type: "number",
            onChange: (e) => setKm(e.target.value),
          }, "nouveau-dossier-km")}
        </div>
        <Textarea
          value={motif} onChange={(e) => setMotif(e.target.value)} rows={3}
          placeholder="Demande du client (ex. bruit de freins à l'avant)"
          className="resize-none" data-testid="nouveau-dossier-motif"
        />

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="nouveau-dossier-annuler">Annuler</Button>
          <Button
            onClick={creer} disabled={busy || (etape === "recherche" && !choisi)}
            className="bg-[#52a675] hover:bg-[#2d5f3f]" data-testid="nouveau-dossier-creer"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ouvrir le dossier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default NouveauDossierDialog;
